import { useMemo, useState } from "react"

import moment from "moment"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Button, Container, Icon, Label, Modal, Popup } from "semantic-ui-react"
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback"
import styled from "styled-components"

import api from "api"
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor"
import { UserChip } from "components/lib/Chips"
import { IdeaFollowButton } from "components/lib/FollowButton"
import { IdeaChallengeHelp, SubmitIdeaButton } from "components/lib/Ideas"
import { PlaceholderItem, Sticky, TooltipButton } from "components/lib/UI"
import useTheme from "theme/useTheme"
import util from "utils/utils"

const MobileContainer = styled.div`
  width: 100%;
  padding: 0 5px;
`

const IdeaStatusBarDiv = styled.div`
  height: ${({ theme }) => (theme.sizes.isMobile ? 40 : 50)}px;
  padding: ${({ theme }) => (theme.sizes.isMobile ? "5px 5px" : "7.5px 15px")};
  background-image: ${({ theme }) => `linear-gradient(to bottom right, ${theme.secondaryColour}, ${util.adjustColour(theme.secondaryColour, 0.5)})`};
  display: flex;
  align-items: center;
  top: 50px;
  width: 100%;
`

const StatusLabel = styled(Label)`
  min-width: max-content;
  height: 27px;
  display: flex !important;
  align-items: center;
`

const StatusbarInnerContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  flex-grow: ${({ $flexGrow }) => $flexGrow};
  min-width: ${({ $flexGrow }) => ($flexGrow === 0 ? "max-content" : 0)};
`

const StatusbarContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .submit-idea-button {
    ${({ theme }) =>
      theme.sizes.isMobile
        ? `
      height: 25px;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 150px !important;
      > i {
        height: 1em !important;
      }
    `
        : ""}
  }
`

const StyledButton = styled(Button)`
  margin-right: 0 !important;
  height: 30px;
  min-width: max-content;
`

const IdeaStatusBar = ({
  idea,
  updateIdea,
  isSaving,
  unsaved,
  isEditing,
  setEditing,
  onSubmit,
  user,
  otherUsers = {},
  websocketConnected,
  scrollToRequired,
  loading,
}) => {
  const { t } = useTranslation()
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const canShowHelpModal = useMemo(() => idea?.ownerChallenge?.stage === "published" && isEditing, [idea, isEditing])
  const otherUsersValues = useMemo(() => Object.values(otherUsers), [otherUsers])
  const theme = useTheme()

  const StatusbarOuterContainer = useMemo(() => (theme.sizes.isMobile ? MobileContainer : Container), [theme])

  const { assessmentState, submitAssessment, isSavingAssessment, assessmentIsSubmissable } = useAssessmentEditor(idea)

  return (
    <Sticky top={50} style={{ zIndex: 300 }}>
      <IdeaStatusBarDiv theme={theme}>
        {canShowHelpModal ? (
          <Modal
            mountNode={document.getElementById("semantic-modal-mount-node")}
            open={helpModalOpen}
            onClose={() => setHelpModalOpen(false)}
            style={{ padding: 20, backgroundColor: "#fff" }}
          >
            <IdeaChallengeHelp challengeId={idea.challenge} closeHelp={() => setHelpModalOpen(false)} />
          </Modal>
        ) : null}

        <StatusbarOuterContainer>
          <StatusbarContainer>
            <StatusbarInnerContainer $flexGrow={1}>
              {loading ? (
                <PlaceholderItem height={25} style={{ width: 150 }} />
              ) : !theme.sizes.isMobile ? (
                idea.isSubmitted ? (
                  <Popup
                    trigger={
                      <StatusLabel size="tiny" color="green">
                        {t("generic.submitted")}
                      </StatusLabel>
                    }
                    content={`Submitted ${moment(idea.submittedAt).format("DD/MM/YYYY HH:mm")}`}
                  />
                ) : (
                  <StatusLabel size="tiny" color="yellow">
                    <Icon name="file outline" /> {t("generic.draft")}
                  </StatusLabel>
                )
              ) : null}
              {canShowHelpModal && !theme.sizes.isMobile ? (
                <Popup
                  header={t("challenges.viewDetails")}
                  trigger={<Button size="mini" icon="eye" onClick={() => setHelpModalOpen(true)} inverted basic />}
                />
              ) : null}
              {idea.ownerChallenge ? (
                <Button
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 250,
                  }}
                  content={`${idea.ownerChallenge?.name}`}
                  size="mini"
                  as={Link}
                  to={`/challenges/${idea.challenge}?noRedirect`}
                  inverted
                  basic
                />
              ) : null}
              {user && !theme.sizes.isMobile && <IdeaFollowButton idea={idea} />}
            </StatusbarInnerContainer>

            <StatusbarInnerContainer $flexGrow={0}>
              {isEditing && !websocketConnected && !theme.sizes.isMobile ? (
                <Popup
                  header="Connection issues"
                  content={`Collaboration features are disabled. Your ${t("generic.ideas")} will still be saved automatically.`}
                  trigger={<Icon color="yellow" size="large" name="warning sign" />}
                />
              ) : null}

              {!theme.sizes.isMobile ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  {otherUsersValues.length ? (
                    <Popup
                      header="Collaborators online"
                      content={otherUsersValues.map((u) => u.profile?.fullName).join(", ")}
                      trigger={
                        <div>
                          {otherUsersValues.map((u, index) => (
                            <ImageWithFallback
                              key={u._id}
                              avatar
                              src={util.avatarUrl(u)}
                              fallbackSrc={util.avatarUrl()}
                              style={{ marginLeft: index === 0 ? 0 : -8, minWidth: 25 }}
                            />
                          ))}
                        </div>
                      }
                    />
                  ) : null}
                </div>
              ) : null}

              {loading ? (
                <PlaceholderItem height={25} style={{ width: 150 }} />
              ) : (
                <>
                  {idea?.isAssessor && !idea?.ownerIdeaTemplate?.assessmentClosed && !assessmentState?.isSubmitted ? (
                    <TooltipButton
                      style={{ marginLeft: 3 }}
                      size="tiny"
                      content="Submit assessment"
                      primary
                      onClick={submitAssessment}
                      loading={isSavingAssessment}
                      disabled={!assessmentIsSubmissable}
                      tooltip={
                        assessmentIsSubmissable
                          ? undefined
                          : "To submit an assessment you either have to complete all sections of the assessment or declare a conflict of interest"
                      }
                    />
                  ) : null}

                  {(util.canEditIdea(user, idea) ||
                    util.canManageChallenge(user, idea.ownerChallenge) ||
                    util.hasPermission(user, "challenge.updateIdeaStatus", idea.challenge)) &&
                  !idea.children ? (
                    <>
                      {!theme.sizes.isMobile ? (
                        idea.isSubmitted &&
                        (util.hasPermission(user, "challenge.editIdeas", idea.challenge) ||
                          util.hasPermission(user, "org.manageChallenges", idea.ownerChallenge?.organisation)) ? (
                          <StyledButton
                            size="tiny"
                            content={isEditing ? "Done editing" : `Edit ${t("generic.idea")}`}
                            onClick={() => {
                              setEditing(!isEditing)
                            }}
                            icon={isEditing ? "check" : "edit"}
                            secondary
                          />
                        ) : !idea.isSubmitted && util.canEditIdea(user, idea) ? (
                          <StyledButton
                            size="tiny"
                            content={t(isEditing ? "ideas.header.preview" : "ideas.header.edit")}
                            onClick={() => {
                              setEditing(!isEditing)
                              if (isEditing) {
                                api.journey.record("ideas", idea._id, "previewedIdea")
                              } else {
                                api.journey.record("ideas", idea._id, "resumedEditingIdea")
                              }
                            }}
                            icon="edit"
                            secondary
                          />
                        ) : null
                      ) : null}

                      <SubmitIdeaButton
                        user={user}
                        idea={idea}
                        updateIdea={updateIdea}
                        onSubmit={onSubmit}
                        scrollToRequired={scrollToRequired}
                        isSaving={isSaving}
                        unsaved={unsaved}
                        isStatusBar={true}
                      />
                    </>
                  ) : (
                    <UserChip inverted user={idea.owner} />
                  )}
                </>
              )}
            </StatusbarInnerContainer>
          </StatusbarContainer>
        </StatusbarOuterContainer>
      </IdeaStatusBarDiv>
    </Sticky>
  )
}

export default IdeaStatusBar
